import { Controller } from "@hotwired/stimulus"
import { search, quoteInEuros } from "../gate"
import { track } from "./analytics_controller"

export default class extends Controller {
  static targets = ["form", "rows", "template", "add", "total", "sum", "error", "limit", "wait", "submit", "results"]
  static values = { search: String, limit: Number, units: Object, rates: Object }

  async connect() {
    await Promise.all(this.rows().filter((row) => this.symbolOf(row)).map((row) => this.price(row)))
    if (!this.problem()) this.calculate(false)
  }

  add() {
    this.rowsTarget.append(this.templateTarget.content.cloneNode(true))
    this.rowsTarget.lastElementChild.querySelector("input[type=search]").focus()
    this.update()
  }

  remove(event) {
    if (this.rows().length === 1) return

    event.currentTarget.closest(".rebalancer-row").remove()
    this.update()
  }

  rows() {
    return Array.from(this.rowsTarget.children)
  }

  edit(event) {
    const row = event.currentTarget.closest(".rebalancer-row")
    row.querySelector(".rebalancer-security__picked").hidden = true
    const query = row.querySelector("input[type=search]")
    query.hidden = false
    query.focus()
  }

  close(event) {
    const row = event.currentTarget.closest(".rebalancer-row")
    row.querySelector(".calculator-search__results").replaceChildren()
    if (!this.symbolOf(row)) return

    row.querySelector("input[type=search]").hidden = true
    row.querySelector("input[type=search]").value = ""
    row.querySelector(".rebalancer-security__picked").hidden = false
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.currentTarget.blur()
    } else if (event.key === "Enter") {
      event.preventDefault()
      event.currentTarget.closest(".rebalancer-row").querySelector(".calculator-search__option:not(:disabled)")?.dispatchEvent(new MouseEvent("mousedown"))
    }
  }

  search(event) {
    if (event.target.type !== "search") return

    const row = event.target.closest(".rebalancer-row")
    const results = row.querySelector(".calculator-search__results")
    const term = event.target.value.trim()
    clearTimeout(this.timer)
    if (term.length < 2) {
      results.replaceChildren()
      return
    }

    this.timer = setTimeout(async () => {
      const securities = await search(term, this.searchValue)
      if (event.target.value.trim() !== term) return

      const taken = this.rows().map((other) => this.symbolOf(other))
      results.replaceChildren(
        ...securities.map((security) => this.suggestion(row, security, taken.includes(security.symbol))),
        this.custom(row, term)
      )
    }, 250)
  }

  suggestion(row, security, added) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "calculator-search__option"
    button.setAttribute("role", "option")
    button.disabled = added

    const symbol = document.createElement("span")
    symbol.className = "calculator-symbol"
    symbol.textContent = security.symbol
    const name = document.createElement("span")
    name.className = "calculator-name"
    name.textContent = [security.name, security.exchange, added && "added"].filter(Boolean).join(" · ")

    button.append(symbol, name)
    button.addEventListener("mousedown", (event) => {
      event.preventDefault()
      this.pick(row, security)
    })
    return button
  }

  custom(row, term) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "calculator-search__option"
    button.setAttribute("role", "option")

    const name = document.createElement("span")
    name.className = "calculator-name"
    name.textContent = `Use “${term}” as a custom holding`

    button.append(name)
    button.addEventListener("mousedown", (event) => {
      event.preventDefault()
      this.fill(row, "", "", "")
      this.setCurrency(row, this.formTarget.elements.currency.value, false)
      row.querySelector("input[type=search]").blur()
      row.querySelector("input[name='holdings[][value]']").focus()
      this.update()
    })
    return button
  }

  pick(row, security) {
    this.fill(row, security.symbol, security.name, "")
    const picked = row.querySelector(".rebalancer-security__picked")
    picked.querySelector(".calculator-symbol").textContent = security.symbol
    picked.querySelector(".calculator-name").textContent = security.name
    row.querySelector("input[type=search]").blur()
    this.errorTarget.hidden = true
    row.querySelector("input[name='holdings[][value]']").focus()
    this.price(row)
    track("rebalancer-symbol", { symbol: security.symbol })
  }

  async price(row) {
    const symbol = this.symbolOf(row)
    try {
      const quote = await quoteInEuros(symbol)
      if (this.symbolOf(row) !== symbol) return

      row.querySelector(".rebalancer-security__price").value = quote.priceEur
      const supported = Boolean(this.unitsValue[quote.currency])
      this.setCurrency(row, supported ? quote.currency : this.formTarget.elements.currency.value, supported)
    } catch {
      row.querySelector(".rebalancer-security__price").value = ""
    }
    this.update()
  }

  fill(row, symbol, name, price) {
    row.querySelector(".rebalancer-security__symbol").value = symbol
    row.querySelector(".rebalancer-security__name").value = name
    row.querySelector(".rebalancer-security__price").value = price
  }

  symbolOf(row) {
    return row.querySelector(".rebalancer-security__symbol").value
  }

  setCurrency(row, currency, fixed) {
    row.querySelector(".rebalancer-security__currency").value = currency
    const unit = row.querySelector(".rebalancer-unit")
    unit.textContent = this.unitsValue[currency] || currency
    unit.classList.toggle("rebalancer-unit--security", fixed)
  }

  currency(event) {
    const currency = event.target.value
    this.element.querySelectorAll(".rebalancer-unit:not(.rebalancer-unit--security)").forEach((element) => {
      element.textContent = this.unitsValue[currency]
      element.closest(".rebalancer-row")?.querySelector(".rebalancer-security__currency")?.setAttribute("value", currency)
    })
    this.templateTarget.content.querySelector(".rebalancer-unit").textContent = this.unitsValue[currency]
    this.templateTarget.content.querySelector(".rebalancer-security__currency").setAttribute("value", currency)
    this.update()
  }

  update() {
    const currency = this.formTarget.elements.currency.value
    const total = this.rows().reduce((sum, row) => {
      const value = Number(row.querySelector("input[name='holdings[][value]']").value) || 0
      return sum + value * (this.ratesValue[row.querySelector(".rebalancer-security__currency").value] || 1)
    }, 0) / this.ratesValue[currency]
    const sum = this.sumOf("target")
    const unit = this.unitsValue[currency]
    this.totalTarget.textContent = `${unit}${new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(total)}`
    this.sumTarget.textContent = `${Math.round(sum * 100) / 100}%`
    this.sumTarget.className = this.balanced(sum) ? "calculator-up" : "rebalancer-warning"
    this.addTarget.hidden = this.rows().length >= this.limitValue
  }

  sumOf(field) {
    return Array.from(this.rowsTarget.querySelectorAll(`input[name="holdings[][${field}]"]`))
      .reduce((sum, input) => sum + (Number(input.value) || 0), 0)
  }

  balanced(sum) {
    return Math.round(sum * 100) === 10000
  }

  problem() {
    if (this.rows().some((row) => !this.symbolOf(row) && !row.querySelector("input[type=search]").value.trim())) return "Name every holding."
    const unpriced = this.rows().filter((row) => this.symbolOf(row) && !row.querySelector(".rebalancer-security__price").value)
    if (unpriced.length > 0) return `Couldn't load a price for ${unpriced.map((row) => this.symbolOf(row)).join(", ")}. Try again in a minute.`
    if (!this.balanced(this.sumOf("target"))) return "Targets must add up to 100%."
  }

  submit(event) {
    event.preventDefault()
    const problem = this.problem()
    this.errorTarget.textContent = problem || ""
    this.errorTarget.hidden = !problem
    if (problem) return

    this.calculate(true)
  }

  async calculate(scroll) {
    this.submitTarget.disabled = true
    const query = new URLSearchParams(new FormData(this.formTarget))
    const response = await fetch(`${this.formTarget.action}?${query}`, { headers: { Accept: "text/html" } })

    if (response.status === 429) {
      track("rebalancer-limited")
      this.rateLimited(Number(response.headers.get("Retry-After")) || 3600)
      return
    }

    this.submitTarget.disabled = false
    if (!response.ok) return

    this.resultsTarget.innerHTML = await response.text()
    track("rebalancer-run", { holdings: this.rows().length, currency: query.get("currency"), selling: query.getAll("allow_selling").includes("1"), contribution: Number(query.get("contribution")) > 0, trigger: scroll ? "submit" : "load" })
    history.replaceState(history.state, "", `${location.pathname}?${query}`)
    if (scroll && this.resultsTarget.getBoundingClientRect().top > window.innerHeight) {
      this.resultsTarget.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  rateLimited(seconds) {
    this.waitTarget.textContent = `${Math.ceil(seconds / 60)} min`
    this.limitTarget.hidden = false
    clearTimeout(this.unlock)
    this.unlock = setTimeout(() => {
      this.limitTarget.hidden = true
      this.submitTarget.disabled = false
    }, seconds * 1000)
  }
}
